import React from 'react';
import { Link } from 'react-router-dom';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';

const PrivacyPage = () => (
  <div className="min-h-screen bg-zinc-950 text-zinc-200" data-testid="privacy-page">
    <header className="border-b border-zinc-800 bg-zinc-900/60 backdrop-blur sticky top-0 z-40">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="text-sm text-zinc-400 hover:text-white">← Tempus Dispatch</Link>
        <div className="flex gap-4 text-xs">
          <Link to="/terms" className="text-zinc-400 hover:text-white">Terms</Link>
          <Link to="/privacy" className="text-white font-medium">Privacy</Link>
        </div>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-4 py-10 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-1">Privacy Policy</h1>
        <p className="text-xs text-zinc-500">Effective: February 1, 2026 · Last updated: February 1, 2026</p>
      </div>

      <Section title="1. Who We Are">
        Tempus Dispatch LLC ("Tempus") provides a SaaS platform for the trucking industry.
        Contact: givi@tempusdispatch.com.
      </Section>

      <Section title="2. Information We Collect">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account data:</strong> name, email, phone, company, MC/DOT number.</li>
          <li><strong>Operational data:</strong> loads posted, bids placed, rate confirmations, invoices.</li>
          <li><strong>Public registry data:</strong> motor carrier and broker information sourced from the FMCSA SAFER database (which is published as public information under 49 CFR § 379).</li>
          <li><strong>Communications:</strong> messages, emails, and call transcripts processed via the platform.</li>
          <li><strong>Usage data:</strong> cookies, IP address, device, browser, pages visited.</li>
        </ul>
      </Section>

      <Section title="3. How We Use Information">
        <ul className="list-disc pl-6 space-y-1">
          <li>To operate, maintain, and improve the platform</li>
          <li>To match loads with drivers and route bids between users</li>
          <li>To send transactional and operational notifications (rate cons, invoices, status updates)</li>
          <li>To send commercial email to motor carriers and brokers about the platform — every commercial email contains our physical address and a one-click unsubscribe link in compliance with the CAN-SPAM Act (15 USC § 7701 et seq.)</li>
          <li>To analyze platform usage and improve features</li>
        </ul>
      </Section>

      <Section title="4. CAN-SPAM & Email Outreach">
        <p>
          Commercial emails sent through Tempus to FMCSA-registered motor carriers and brokers
          comply with the CAN-SPAM Act of 2003. Each commercial message contains:
        </p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>A truthful "From" address and subject line</li>
          <li>Our valid physical postal address</li>
          <li>A clear, one-click unsubscribe link honored within 10 business days</li>
          <li>A disclosure that contact information was obtained from the FMCSA public registry</li>
        </ul>
        <p className="mt-2">
          To unsubscribe at any time visit <Link to="/unsubscribe" className="underline text-blue-400">tempusdispatch.com/unsubscribe</Link>.
        </p>
      </Section>

      <Section title="5. Sharing">
        We do not sell personal information. We share data with:
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>Service providers (AWS, Stripe, MongoDB, email/SMS infrastructure) under contract</li>
          <li>Other platform users you transact with (your name, company, MC#, contact details — necessary to complete a load)</li>
          <li>Law enforcement when required by valid legal process</li>
        </ul>
      </Section>

      <Section title="6. Data Retention">
        Account and transaction data is retained for the life of the account and for up to 7 years after closure for regulatory and tax purposes.
      </Section>

      <Section title="7. Your Rights">
        You may request access, correction, or deletion of your data by emailing privacy@tempusdispatch.com.
        California, Virginia, and EU residents have additional rights under CCPA / VCDPA / GDPR.
      </Section>

      <Section title="8. Security">
        We use TLS in transit, encryption at rest, role-based access controls, and routine logging. No system is perfectly secure; we encourage strong passwords and 2FA where available.
      </Section>

      <Section title="9. Children">
        The platform is intended for B2B users 18+; we do not knowingly collect data from minors.
      </Section>

      <Section title="10. Changes">
        We may update this policy. Material changes will be announced on the platform or via email.
      </Section>
    </main>

    <PlatformDisclaimer />
  </div>
);

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="text-lg font-semibold text-white">{title}</h2>
    <div className="text-sm text-zinc-300 leading-relaxed">{children}</div>
  </section>
);

export default PrivacyPage;
